// Reglas y formatos de las instancias de evaluación (iguales a BE.Domain/Helpers/EvaluacionesHelper.cs).
// Escala 1 a 10 con hasta 2 decimales; aprueba con 6; estado académico por promedio simple.

export const NOTA_MINIMA = 1;
export const NOTA_MAXIMA = 10;
export const NOTA_APROBACION = 6;

export const ESTADOS_ACADEMICOS = {
  aprobado: { label: 'Aprobado', tone: 'success', icon: 'fa-check-circle' },
  en_riesgo: { label: 'En riesgo', tone: 'danger', icon: 'fa-exclamation-circle' },
  sin_notas: { label: 'Sin notas', tone: 'muted', icon: 'fa-minus-circle' }
};

const pick = (source, lower, upper) => source?.[lower] ?? source?.[upper];
const toNumberOrNull = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

// Acepta "7", "7,5", "7.25". Vacío = sin nota. Devuelve { value, error }.
export const parseNotaInput = (text) => {
  const raw = String(text ?? '').trim();
  if (!raw) return { value: null, error: '' };
  if (!/^\d{1,2}([.,]\d{1,2})?$/.test(raw)) {
    return { value: null, error: 'Usá un número de 1 a 10 (hasta 2 decimales).' };
  }
  const value = Number(raw.replace(',', '.'));
  if (value < NOTA_MINIMA || value > NOTA_MAXIMA) {
    return { value: null, error: 'La nota tiene que estar entre 1 y 10.' };
  }
  return { value, error: '' };
};

// 7.5 -> "7,5"; 8 -> "8"; null -> ''.
export const formatNota = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return Number(value).toLocaleString('es-AR', { maximumFractionDigits: 2 });
};

export const formatPromedio = (value) => (value === null || value === undefined ? '—' : Number(value).toLocaleString('es-AR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}));

export const getEstadoAcademico = (estado) => ESTADOS_ACADEMICOS[estado] || ESTADOS_ACADEMICOS.sin_notas;

export const isNotaDesaprobada = (nota) => nota !== null && nota !== undefined && Number(nota) < NOTA_APROBACION;

// "2026-10-20T18:00:00" (hora local, sin zona) -> "2026-10-20T18:00" para el input datetime-local.
export const toDateTimeLocalValue = (value) => String(value || '').slice(0, 16);

export const formatFechaHora = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return '';
  const [, y, m, d, h, mi] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(mi));
  return `${date.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })} ${h}:${mi}`;
};

export const getAlumnoNombre = (alumno) => {
  const apellido = pick(alumno, 'apellido', 'Apellido');
  const nombre = pick(alumno, 'nombre', 'Nombre');
  if (apellido || nombre) return [apellido, nombre].filter(Boolean).join(', ');
  return pick(alumno, 'displayName', 'DisplayName') || pick(alumno, 'email', 'Email') || 'Alumno';
};

export const normalizeEvaluacion = (item) => ({
  id: String(pick(item, 'id', 'Id') || ''),
  nombre: pick(item, 'nombre', 'Nombre') || '',
  descripcion: pick(item, 'descripcion', 'Descripcion') || '',
  fechaHora: pick(item, 'fechaHora', 'FechaHora') || '',
  idCursoMateria: pick(item, 'idCursoMateria', 'IdCursoMateria') ? String(pick(item, 'idCursoMateria', 'IdCursoMateria')) : '',
  nombreMateria: pick(item, 'nombreMateria', 'NombreMateria') || '',
  totalAlumnos: Number(pick(item, 'totalAlumnos', 'TotalAlumnos') || 0),
  cantidadCalificados: Number(pick(item, 'cantidadCalificados', 'CantidadCalificados') || 0),
  cantidadAusentes: Number(pick(item, 'cantidadAusentes', 'CantidadAusentes') || 0),
  promedio: toNumberOrNull(pick(item, 'promedio', 'Promedio'))
});

export const normalizePlanillaAlumno = (item) => ({
  idAlumno: String(pick(item, 'idAlumno', 'IdAlumno') || ''),
  nombre: getAlumnoNombre(item),
  email: pick(item, 'email', 'Email') || '',
  nota: toNumberOrNull(pick(item, 'nota', 'Nota')),
  ausente: Boolean(pick(item, 'ausente', 'Ausente')),
  comentario: pick(item, 'comentario', 'Comentario') || '',
  fechaModificacion: pick(item, 'fechaModificacion', 'FechaModificacion') || ''
});

export const normalizeResumen = (item) => {
  const evaluaciones = (pick(item, 'evaluaciones', 'Evaluaciones') || []).map((e) => ({
    id: String(pick(e, 'id', 'Id') || ''),
    nombre: pick(e, 'nombre', 'Nombre') || '',
    fechaHora: pick(e, 'fechaHora', 'FechaHora') || '',
    idCursoMateria: pick(e, 'idCursoMateria', 'IdCursoMateria') ? String(pick(e, 'idCursoMateria', 'IdCursoMateria')) : '',
    nombreMateria: pick(e, 'nombreMateria', 'NombreMateria') || '',
    promedio: toNumberOrNull(pick(e, 'promedio', 'Promedio')),
    cantidadCalificados: Number(pick(e, 'cantidadCalificados', 'CantidadCalificados') || 0)
  }));
  const alumnos = (pick(item, 'alumnos', 'Alumnos') || []).map((a) => ({
    idAlumno: String(pick(a, 'idAlumno', 'IdAlumno') || ''),
    nombre: getAlumnoNombre(a),
    email: pick(a, 'email', 'Email') || '',
    promedio: toNumberOrNull(pick(a, 'promedio', 'Promedio')),
    estado: pick(a, 'estado', 'Estado') || 'sin_notas',
    cantidadNotas: Number(pick(a, 'cantidadNotas', 'CantidadNotas') || 0),
    cantidadAusentes: Number(pick(a, 'cantidadAusentes', 'CantidadAusentes') || 0),
    notas: (pick(a, 'notas', 'Notas') || []).map((n) => ({
      idEvaluacion: String(pick(n, 'idEvaluacion', 'IdEvaluacion') || ''),
      nota: toNumberOrNull(pick(n, 'nota', 'Nota')),
      ausente: Boolean(pick(n, 'ausente', 'Ausente')),
      comentario: pick(n, 'comentario', 'Comentario') || ''
    })),
    porMateria: (pick(a, 'porMateria', 'PorMateria') || []).map((m) => ({
      idCursoMateria: String(pick(m, 'idCursoMateria', 'IdCursoMateria') || ''),
      nombreMateria: pick(m, 'nombreMateria', 'NombreMateria') || '',
      promedio: toNumberOrNull(pick(m, 'promedio', 'Promedio')),
      estado: pick(m, 'estado', 'Estado') || 'sin_notas'
    }))
  }));
  const totales = pick(item, 'totales', 'Totales') || {};
  return {
    evaluaciones,
    alumnos,
    totales: {
      promedioGeneral: toNumberOrNull(pick(totales, 'promedioGeneral', 'PromedioGeneral')),
      aprobados: Number(pick(totales, 'aprobados', 'Aprobados') || 0),
      enRiesgo: Number(pick(totales, 'enRiesgo', 'EnRiesgo') || 0),
      sinNotas: Number(pick(totales, 'sinNotas', 'SinNotas') || 0),
      totalAlumnos: Number(pick(totales, 'totalAlumnos', 'TotalAlumnos') || 0),
      totalEvaluaciones: Number(pick(totales, 'totalEvaluaciones', 'TotalEvaluaciones') || 0)
    }
  };
};

// Título de columna de una instancia en el resumen ("Matemática · Parcial 1" en colegios).
export const getEvaluacionColumnTitle = (evaluacion) => (
  evaluacion.nombreMateria ? `${evaluacion.nombreMateria} · ${evaluacion.nombre}` : evaluacion.nombre
);

// Estado académico de la cuenta particular (GET /particulares/academico).
const normalizeAsistenciaResumen = (item) => {
  const a = item || {};
  return {
    total: Number(pick(a, 'total', 'Total') || 0),
    presentes: Number(pick(a, 'presentes', 'Presentes') || 0),
    tardes: Number(pick(a, 'tardes', 'Tardes') || 0),
    ausentes: Number(pick(a, 'ausentes', 'Ausentes') || 0),
    justificados: Number(pick(a, 'justificados', 'Justificados') || 0),
    porcentaje: toNumberOrNull(pick(a, 'porcentaje', 'Porcentaje')),
    ausenciasConsecutivas: Number(pick(a, 'ausenciasConsecutivas', 'AusenciasConsecutivas') || 0)
  };
};

// Asistencia: debajo del 75% se considera baja.
export const ASISTENCIA_MINIMA = 75;
export const formatPorcentaje = (value) => (value === null || value === undefined ? '—' : `${Number(value).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`);

export const normalizeEstadoAcademicoCuenta = (item) => {
  const totales = pick(item, 'totales', 'Totales') || {};
  return {
    modoColegio: Boolean(pick(item, 'modoColegio', 'ModoColegio')),
    alertas: (pick(item, 'alertas', 'Alertas') || []).map((a) => ({
      idAlumno: String(pick(a, 'idAlumno', 'IdAlumno') || ''),
      alumnoNombre: pick(a, 'alumnoNombre', 'AlumnoNombre') || 'Alumno',
      divisiones: pick(a, 'divisiones', 'Divisiones') || '',
      tipo: pick(a, 'tipo', 'Tipo') || '',
      severidad: pick(a, 'severidad', 'Severidad') || 'media',
      mensaje: pick(a, 'mensaje', 'Mensaje') || ''
    })),
    totales: {
      tieneAsistencias: Boolean(pick(totales, 'tieneAsistencias', 'TieneAsistencias')),
      porcentajeAsistencia: toNumberOrNull(pick(totales, 'porcentajeAsistencia', 'PorcentajeAsistencia')),
      alumnosConAlertas: Number(pick(totales, 'alumnosConAlertas', 'AlumnosConAlertas') || 0),
      totalCursos: Number(pick(totales, 'totalCursos', 'TotalCursos') || 0),
      totalAlumnos: Number(pick(totales, 'totalAlumnos', 'TotalAlumnos') || 0),
      totalEvaluaciones: Number(pick(totales, 'totalEvaluaciones', 'TotalEvaluaciones') || 0),
      promedioGeneral: toNumberOrNull(pick(totales, 'promedioGeneral', 'PromedioGeneral')),
      alumnosAprobados: Number(pick(totales, 'alumnosAprobados', 'AlumnosAprobados') || 0),
      alumnosEnRiesgo: Number(pick(totales, 'alumnosEnRiesgo', 'AlumnosEnRiesgo') || 0),
      alumnosSinNotas: Number(pick(totales, 'alumnosSinNotas', 'AlumnosSinNotas') || 0)
    },
    cursos: (pick(item, 'cursos', 'Cursos') || []).map((c) => ({
      id: String(pick(c, 'id', 'Id') || ''),
      nombre: pick(c, 'nombre', 'Nombre') || 'Curso',
      divisionNombre: pick(c, 'divisionNombre', 'DivisionNombre') || '',
      totalAlumnos: Number(pick(c, 'totalAlumnos', 'TotalAlumnos') || 0),
      totalEvaluaciones: Number(pick(c, 'totalEvaluaciones', 'TotalEvaluaciones') || 0),
      promedioGeneral: toNumberOrNull(pick(c, 'promedioGeneral', 'PromedioGeneral')),
      aprobados: Number(pick(c, 'aprobados', 'Aprobados') || 0),
      enRiesgo: Number(pick(c, 'enRiesgo', 'EnRiesgo') || 0),
      sinNotas: Number(pick(c, 'sinNotas', 'SinNotas') || 0),
      porcentajeAsistencia: toNumberOrNull(pick(c, 'porcentajeAsistencia', 'PorcentajeAsistencia')),
      alumnosBajaAsistencia: Number(pick(c, 'alumnosBajaAsistencia', 'AlumnosBajaAsistencia') || 0)
    })),
    alumnos: (pick(item, 'alumnos', 'Alumnos') || []).map((a) => ({
      id: String(pick(a, 'id', 'Id') || ''),
      nombre: getAlumnoNombre(a),
      email: pick(a, 'email', 'Email') || '',
      divisiones: pick(a, 'divisiones', 'Divisiones') || '',
      promedioGeneral: toNumberOrNull(pick(a, 'promedioGeneral', 'PromedioGeneral')),
      estado: pick(a, 'estado', 'Estado') || 'sin_notas',
      cursosEnRiesgo: Number(pick(a, 'cursosEnRiesgo', 'CursosEnRiesgo') || 0),
      asistencia: normalizeAsistenciaResumen(pick(a, 'asistencia', 'Asistencia')),
      cursos: (pick(a, 'cursos', 'Cursos') || []).map((c) => ({
        idCurso: String(pick(c, 'idCurso', 'IdCurso') || ''),
        nombreCurso: pick(c, 'nombreCurso', 'NombreCurso') || 'Curso',
        divisionNombre: pick(c, 'divisionNombre', 'DivisionNombre') || '',
        promedio: toNumberOrNull(pick(c, 'promedio', 'Promedio')),
        estado: pick(c, 'estado', 'Estado') || 'sin_notas',
        cantidadNotas: Number(pick(c, 'cantidadNotas', 'CantidadNotas') || 0),
        cantidadAusentes: Number(pick(c, 'cantidadAusentes', 'CantidadAusentes') || 0)
      }))
    })),
    divisiones: (pick(item, 'divisiones', 'Divisiones') || []).map((d) => ({
      id: String(pick(d, 'id', 'Id') || ''),
      nombreCompleto: pick(d, 'nombreCompleto', 'NombreCompleto') || 'División',
      porcentajeAsistencia: toNumberOrNull(pick(d, 'porcentajeAsistencia', 'PorcentajeAsistencia')),
      totalCursos: Number(pick(d, 'totalCursos', 'TotalCursos') || 0),
      totalAlumnos: Number(pick(d, 'totalAlumnos', 'TotalAlumnos') || 0),
      promedioGeneral: toNumberOrNull(pick(d, 'promedioGeneral', 'PromedioGeneral')),
      aprobados: Number(pick(d, 'aprobados', 'Aprobados') || 0),
      enRiesgo: Number(pick(d, 'enRiesgo', 'EnRiesgo') || 0),
      sinNotas: Number(pick(d, 'sinNotas', 'SinNotas') || 0)
    }))
  };
};
